import React, { useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

// Maps the first path segment after /app/ to the navKey defined in Admin → Menu Visibility.
// If the pathname matches a hidden navKey, the guard redirects the user to /app/reimbursements.
// URL patterns that do NOT map (e.g., /app itself) fall through to their normal Route.
const PATH_TO_NAVKEY = {
  dashboard: "dashboard",
  planner: "planner",
  pto: "pto",
  travel: "travel",
  reimbursements: "reimbursements",
  loans: "loans",
  trainings: "trainings",
  performance: "performance",
  hiring: "hiring",
  onboarding: "onboarding",
  offboarding: "offboarding",
  directory: "directory",
  departments: "departments",
  "org-chart": "org-chart",
  cockpit: "cockpit",
  approvals: "approvals",
  timesheet: "timesheet",
  documents: "documents",
  knowledge: "knowledge",
  copilot: "copilot",
  "salary-matrix": "salary-matrix",
  admin: "admin",
  manual: "manual",
  profile: "profile",
};

const FALLBACK_PATH = "/app/reimbursements";

export default function NavGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [hiddenSet, setHiddenSet] = useState(null); // null = still loading; Set = loaded

  useEffect(() => {
    if (!user) return;
    // admin always sees everything — skip fetch
    if (user.role === "admin") {
      setHiddenSet(new Set());
      return;
    }
    api.get("/nav-visibility/me")
      .then((r) => setHiddenSet(new Set(r.data?.hidden_items || [])))
      .catch(() => setHiddenSet(new Set()));
  }, [user]);

  // Wait for hidden set to load — avoid flicker/false-block on first render
  if (hiddenSet === null) return null;

  // Derive current navKey from the URL: first segment after /app/
  const match = location.pathname.match(/^\/app\/([^/]+)/);
  const seg = match ? match[1] : null;
  const currentNavKey = seg ? PATH_TO_NAVKEY[seg] : null;

  // If the current page is explicitly hidden for this user, redirect
  if (currentNavKey && hiddenSet.has(currentNavKey) && location.pathname !== FALLBACK_PATH) {
    return <Navigate to={FALLBACK_PATH} replace />;
  }

  return children;
}
